// Status code mappings and utilities

import type { CouponStatusType, RemarkType, GroupPosition } from './types';

/**
 * Booking status codes (HK, HL, etc.)
 */
export const BOOKING_STATUS_CODES: Record<string, { name: string; color: 'green' | 'amber' | 'slate' }> = {
  HK: { name: 'Confirmed', color: 'green' },
  HL: { name: 'Waitlist', color: 'amber' },
  KK: { name: 'Confirming', color: 'amber' },
  KL: { name: 'Confirming from waitlist', color: 'amber' },
  TK: { name: 'New flight times', color: 'amber' },
  US: { name: 'Unable, waitlisted', color: 'amber' },
  UU: { name: 'Unable, waitlisted', color: 'amber' },
  TL: { name: 'Waitlisted, new times', color: 'amber' },
  HN: { name: 'Holding need', color: 'slate' },
  TN: { name: 'Holding needed', color: 'slate' },
  HX: { name: 'Cancelled', color: 'slate' },
  RR: { name: 'Reconfirmed', color: 'green' },
  NO: { name: 'No action', color: 'slate' },
};

/**
 * Get booking status description
 */
export function getStatusDescription(code: string): string {
  return BOOKING_STATUS_CODES[code]?.name || code;
}

/**
 * Get booking status color class
 */
export function getStatusColor(code: string): 'green' | 'amber' | 'slate' {
  return BOOKING_STATUS_CODES[code]?.color || 'slate';
}

/**
 * Coupon status mappings
 */
export const COUPON_STATUS: Record<CouponStatusType, { label: string; variant: 'success' | 'warning' | 'secondary' }> = {
  open: { label: 'OPEN', variant: 'success' },
  flownOrUsed: { label: 'FLOWN', variant: 'warning' },
  exchangedOrReissued: { label: 'EXCHANGED', variant: 'secondary' },
  checkedIn: { label: 'CHECKED IN', variant: 'success' },
  airportControl: { label: 'AIRPORT CONTROL', variant: 'success' },
};

/**
 * Get coupon status info
 */
export function getCouponStatusInfo(status: CouponStatusType): { label: string; variant: 'success' | 'warning' | 'secondary' } {
  return COUPON_STATUS[status] || { label: status.toUpperCase(), variant: 'secondary' };
}

/**
 * Remark type to color mapping
 */
export const REMARK_TYPE_COLORS: Record<RemarkType, string> = {
  GeneralRemark: 'slate-400',
  ConfidentialRemark: 'red-500',
  CorporateRemark: 'orange-500',
};

/**
 * Get remark border color class
 */
export function getRemarkBorderColor(remarkType: RemarkType): string {
  return REMARK_TYPE_COLORS[remarkType] || 'slate-400';
}

/**
 * SSR code to color mapping
 */
export function getSSRBorderColor(code: string): string {
  if (code === 'OTHS') return 'orange-500';
  return 'slate-400';
}

/**
 * Common SSR code descriptions
 */
export const SSR_CODE_DESCRIPTIONS: Record<string, string> = {
  DOCS: 'Travel Document Info',
  DOCA: 'Address Information',
  DOCO: 'Other Travel Related Info',
  CTCE: 'Contact Email',
  CTCM: 'Contact Mobile',
  CTCR: 'Contact Refusal',
  FQTR: 'Frequent Traveller Redemption',
  FQTV: 'Frequent Traveller Info',
  OTHS: 'Other Information',
  INFT: 'Infant',
  CHLD: 'Child',
  MEAL: 'Meal Request',
  SEAT: 'Seat Request',
  WCHR: 'Wheelchair Ramp',
  WCHC: 'Wheelchair Cabin',
  WCHS: 'Wheelchair Steps',
};

/**
 * Get SSR description
 */
export function getSSRDescription(code: string): string {
  return SSR_CODE_DESCRIPTIONS[code] || code;
}

/**
 * Determine group position for sequential items
 */
export function getGroupPosition(
  currentId: string,
  index: number,
  items: Array<{ id: string }>
): GroupPosition {
  const getOTNumber = (id: string): number | null => {
    const match = id.match(/OT(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  const currentNum = getOTNumber(currentId);
  const prevNum = index > 0 ? getOTNumber(items[index - 1].id) : null;
  const nextNum = index < items.length - 1 ? getOTNumber(items[index + 1].id) : null;

  if (currentNum === null) return 'single';

  const isPrevSequential = prevNum !== null && currentNum === prevNum + 1;
  const isNextSequential = nextNum !== null && currentNum === nextNum - 1;

  if (isPrevSequential && isNextSequential) return 'middle';
  if (isPrevSequential) return 'last';
  if (isNextSequential) return 'first';
  return 'single';
}

/**
 * Check if remark should be faded (15BELOW remarks)
 */
export function isRemarkFaded(freetext: string): boolean {
  return freetext.includes('15BELOW');
}

/**
 * Cabin class display names
 */
export const CABIN_CLASSES: Record<string, string> = {
  economy: 'Economy',
  premium_economy: 'Premium Economy',
  business: 'Business',
  first: 'First',
};

/**
 * Get cabin class display name
 */
export function getCabinClassName(cabin: string): string {
  return CABIN_CLASSES[cabin] || cabin;
}

/**
 * Payment method icons
 */
export const PAYMENT_ICONS: Record<string, string> = {
  AX: 'American Express',
  VI: 'Visa',
  MC: 'Mastercard',
  CA: 'Mastercard',
  CC: 'Credit Card',
};

/**
 * Get payment method name
 */
export function getPaymentMethodName(vendorCode: string | undefined): string {
  return vendorCode ? (PAYMENT_ICONS[vendorCode] || vendorCode) : 'Unknown';
}
